import React, { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import { Box, Button, Autocomplete, TextField } from "@mui/material";
import { isValidInput } from "../../utils/validations";
import { Tasks, TasksRespnse } from "../../types/General";
import useTranslation from "../../hooks/traslation";
import { useNavigate, useSearchParams } from "react-router-dom";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import { BOOKING_STATUS } from "../../utils/enums";
import MainContainer from "../../layout/MainContainer";
import usePermission from "../../hooks/usePermission";
import { showError } from "../../constants/toasts";
import { useLazyGetTaskQuery } from "../../services/task";
import moment from "moment";
import { useLazyGetTaskListQuery } from "../../services/users";

type StatusOption = {
  label: string;
  value: number;
};

function ManageTasks() {
  const { isAdd, isDelete } = usePermission({
    label: "Manage Tasks",
    page_permission: "isView",
  });

  const [rows, setRows] = useState<Tasks[]>();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<number | null>();
  const translation = useTranslation();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [getTaskById] = useLazyGetTaskListQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<TasksRespnse[]>([]);

  const [getTasks] = useLazyGetTaskQuery();

  const getAllTasks: any = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        limit: limit,
        search: debouncedSearchTerm.trim(),
      };

      if (selectedStatus !== null) {
        params.status = selectedStatus;
      }

      const response = await getTasks(params).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setData(response?.data[0]?.list || []);
        setCount(response?.data[0]?.totalCount);
      } else {
        setIsLoading(false);
        setData([]);
      }
    } catch (error: any) {
      setIsLoading(false);
      showError(error?.data?.message || "");
      setData([]);
    }
  };

  const getTaskOfEmployer = async (id: string) => {
    setLoading(true);
    try {
      const response = await getTaskById({
        id,
        page,
        limit: 10,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setRows(response?.data?.list || []);
        setCount(response?.data?.totalCount || null);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };
  const navigate = useNavigate();

  const columns: GridColDef<TasksRespnse>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "category",
      headerName: "Category Details",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block" style={{ width: '100%', wordBreak: 'break-word' }}>
            <p style={{ margin: 0, whiteSpace: 'normal' }}>
              <b>{row.category?.name || "-"}</b>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "employer",
      headerName: "Employer Details",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-employers/detail/${row?.employerId?._id}`)
            }
          >
            <figure>
              <img
                src={
                  row.employerId?.image || "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{`${row?.employerId?.firstName} ${row?.employerId?.lastName}`}</b>
              <span style={{ display: 'block', maxWidth: 180, wordBreak: 'break-all', whiteSpace: 'normal' }} title={row?.employerId?.email || "-"}>{row?.employerId?.email || "-"}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "freelancer",
      headerName: "Freelancer Details",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() => {
              if (row.freelancerId?._id) {
                navigate(`/manage-freelancer/detail/${row?.freelancerId?._id}`);
              }
            }}
          >
            <figure className="no_radius">
              <img
                src={
                  row?.freelancerId?.image || "/static/images/placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{`${row?.freelancerId?.firstName || "-"} ${
                row?.freelancerId?.lastName || ""
              }`}</b>
              <span style={{ display: 'block', maxWidth: 180, wordBreak: 'break-all', whiteSpace: 'normal' }} title={row?.freelancerId?.email || "-"}>{row?.freelancerId?.email || "-"}</span>
            </p>
          </div>
        );
      },
    },
    {
      minWidth: 150,
      field: "amount",
      headerName: "Amount",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_primary">
            <strong>AED {Number(row?.total).toFixed(2)}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 200,
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const statusKey = Object.keys(BOOKING_STATUS).find(
          (key) =>
            BOOKING_STATUS[key as keyof typeof BOOKING_STATUS] === row?.status
        );
        const formatStatus = (text: string | undefined) => {
          if (!text) return "";
          return text
            .replace(/_/g, " ")
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        };
        return <p>{formatStatus(statusKey)}</p>;
      },
    },
    {
      minWidth: 200,
      field: "date",
      headerName: "Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return moment(row?.date).format("DD MMMM YYYY, hh:mm A");
      },
    },
    {
      minWidth: 130,
      field: "detail",
      headerName: "Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const userId = row?.employerId?._id || row?.freelancerId?._id;
        return (
          <Button
            className="btn sm btn_primary"
            onClick={() => navigate(`/task_details/${userId}/${row?._id}`)}
          >
            View
          </Button>
        );
      },
    },
  ];

  const statusOptions: StatusOption[] = Object.entries(BOOKING_STATUS)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key
        .replace(/_/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: value as number,
    }));

  useEffect(() => {
    if (id) {
      getTaskOfEmployer(id);
    }
  }, [id, page, debouncedSearchTerm, limit]);

  useEffect(() => {
    getAllTasks();
  }, [page, debouncedSearchTerm, limit, selectedStatus]);
  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Tasks</h1>
        <div className="cards">
          <Box
            className="search_box"
            style={{ marginBottom: "20px", display: "flex", gap: "20px" }}
          >
            <SearchBar
              placeholder={translation.user.SearchHere}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Autocomplete
              className="users_autocomplete"
              options={statusOptions}
              value={
                selectedStatus
                  ? statusOptions.find(
                      (option) => option.value === selectedStatus
                    ) || null
                  : null
              }
              onChange={(_, newValue) => {
                setSelectedStatus(newValue?.value || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Status"
                  fullWidth
                  className="text_field"
                  sx={{ width: "300px" }}
                />
              )}
            />
          </Box>
          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={data}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
            {data.length !== 0 && (
              <div className="table_footer">
                <div className="pagination_info">
                  <span className="total_records">Showing {data.length} of {count} records</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainContainer>
    // </CustomTabPanel>
  );
}

export default ManageTasks;
